import { ReactElement, useMemo, useCallback, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { formatTranslation } from 'intl';

import { MemoComplianceAnswerOptionSelector } from '../ComplianceCheckResultSelector';
import { ProtocolItemCommentAndAttachments } from '../ProtocolItemCommentAndAttachments';
import {
  ChecklistItemAnswerModel,
  ComplianceChecklistAnswerModel,
} from 'shared/domain/template/inspectionTemplateModel';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { ComplianceAnswerOption } from 'shared/domain/compliance/complianceAnswerOption';
import {
  isComplianceChecklistAnswer,
  isNumericChecklistAnswer,
  isTextChecklistAnswer,
  isOptionSelectionChecklistAnswer,
} from 'shared/domain/template/checklistAnswerGuards';
import { NumericAnswerInput } from '../ProtocolItemAnswers/NumericAnswerInput';
import { TextAnswerInput } from '../ProtocolItemAnswers/TextAnswerInput';
import { OptionSelectionInput } from '../ProtocolItemAnswers/OptionSelectionInput';
import {
  ProtocolAnswer,
  findProtocolAnswerByChecklistAnswerId,
  isComplianceProtocolAnswer,
  isNumericProtocolAnswer,
  isTextProtocolAnswer,
  isOptionSelectionProtocolAnswer,
} from 'shared/domain/protocolAnswer';
import {
  useAtomicPersist,
  useInspectionFieldDisabled,
  isInspectionFieldDisabled,
} from 'store/inspection';
import {
  useProtocolModelForChecklistItem,
  useProtocolOnViewForChecklistItem,
} from 'components/inspection/protocolLookup';
import { MemoForbiddenTooltip } from 'components/general/ForbiddenTooltip/ForbiddenTooltip';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import {
  containerSx,
  answersSectionSx,
  commentSectionSx,
  answerFieldHeaderSx,
  answerStackSx,
  complianceToggleWrapSx,
} from './styles';
import {
  answerLabelSx,
  requiredIndicatorSx,
} from '../ProtocolItemAnswers/styles';
import { InspectionTranslationKeys } from 'intl/keys';
import {
  buildComplianceAnswerFormKey,
  buildNumericAnswerFormKey,
  buildTextAnswerFormKey,
  buildOptionSelectionAnswerFormKey,
} from '../protocolAnswerFormKeys';
import { usePendingSaveOnUnmount } from './usePendingSaveOnUnmount';
import { NotApplicableToggle } from 'components/inspection/NotApplicableToggle';

function AnswerFieldHeader({
  label,
  isRequired,
  toggle,
}: {
  label: string;
  isRequired?: boolean;
  toggle?: ReactElement;
}): ReactElement {
  return (
    <Box sx={answerFieldHeaderSx}>
      <Typography component='p' sx={{ ...answerLabelSx, flex: 1, minWidth: 0 }}>
        {label}
        {isRequired ? (
          <Box component='span' sx={requiredIndicatorSx}>*</Box>
        ) : null}
      </Typography>
      {toggle}
    </Box>
  );
}

interface ProtocolItemAnswersFormProps {
  checklistItemId: string;
  allExpanded?: boolean;
  answerConfigs?: ChecklistItemAnswerModel[];
}

export function ProtocolItemAnswersForm({
  checklistItemId,
  allExpanded,
  answerConfigs = [],
}: ProtocolItemAnswersFormProps): ReactElement {
  const intl = useIntl();
  const { values, setValues } = useInputForm();
  const protocolItemFromModel = useProtocolModelForChecklistItem(checklistItemId);
  const protocolItemFromView = useProtocolOnViewForChecklistItem(checklistItemId);
  const {
    saveProtocolAnswer,
    saveProtocolAnswerApplicability,
  } = useAtomicPersist();
  const resolveFieldDisabled = useInspectionFieldDisabled();
  const fieldDisabled = resolveFieldDisabled(false);
  const disabled = isInspectionFieldDisabled(fieldDisabled);
  const disabledReason =
    typeof fieldDisabled === 'object' ? fieldDisabled.reason : undefined;

  const protocolItem = useMemo(() => {
    if (!protocolItemFromView) {
      return undefined;
    }

    return {
      ...protocolItemFromView,
      answers: protocolItemFromModel?.answers ?? protocolItemFromView.answers,
    };
  }, [protocolItemFromModel, protocolItemFromView]);

  const activeAnswers = useMemo(() => {
    return answerConfigs.filter((a) => !('deleted' in a && a.deleted));
  }, [answerConfigs]);

  const { complianceAnswers, otherAnswers } = useMemo(() => {
    const compliance: ComplianceChecklistAnswerModel[] = [];
    const other: ChecklistItemAnswerModel[] = [];

    activeAnswers.forEach((answer) => {
      if (isComplianceChecklistAnswer(answer)) {
        compliance.push(answer);
      } else {
        other.push(answer);
      }
    });

    return { complianceAnswers: compliance, otherAnswers: other };
  }, [activeAnswers]);

  const getComplianceFormKey = useCallback(
    (answerId: string) => buildComplianceAnswerFormKey(checklistItemId, answerId),
    [checklistItemId]
  );

  const firstComplianceAnswer = useMemo(
    () => complianceAnswers[0],
    [complianceAnswers]
  );

  const getNumericFormKey = useCallback(
    (answerId: string) => buildNumericAnswerFormKey(checklistItemId, answerId),
    [checklistItemId]
  );

  const getTextFormKey = useCallback(
    (answerId: string) => buildTextAnswerFormKey(checklistItemId, answerId),
    [checklistItemId]
  );

  const getOptionSelectionFormKey = useCallback(
    (answerId: string) => buildOptionSelectionAnswerFormKey(checklistItemId, answerId),
    [checklistItemId]
  );

  const valuesRef = useRef(values);
  valuesRef.current = values;

  const persistProtocolAnswer = useCallback(
    (answerId: string, answerType: AnswerType, value: unknown) => {
      if (disabled) {
        return;
      }

      if (
        answerType === AnswerType.Compliance &&
        value === ComplianceAnswerOption.notApplicable
      ) {
        return;
      }

      void saveProtocolAnswer(checklistItemId, answerId, answerType, value);
    },
    [checklistItemId, disabled, saveProtocolAnswer]
  );

  const persistProtocolAnswerRef = useRef(persistProtocolAnswer);
  persistProtocolAnswerRef.current = persistProtocolAnswer;

  const { track: trackPendingSave, persistNow: persistSaveNow } =
    usePendingSaveOnUnmount();

  const persistKey = useCallback(
    (answerId: string, answerType: AnswerType) =>
      `${answerId}:${answerType}`,
    []
  );

  const trackPersistProtocolAnswer = useCallback(
    (answerId: string, answerType: AnswerType, value: unknown) => {
      trackPendingSave(persistKey(answerId, answerType), () =>
        persistProtocolAnswerRef.current(answerId, answerType, value)
      );
    },
    [persistKey, trackPendingSave]
  );

  const flushPersistProtocolAnswer = useCallback(
    (answerId: string, answerType: AnswerType, value: unknown) => {
      persistSaveNow(persistKey(answerId, answerType), () =>
        persistProtocolAnswerRef.current(answerId, answerType, value)
      );
    },
    [persistKey, persistSaveNow]
  );

  const isAnswerApplicable = useCallback(
    (answerId: string): boolean =>
      findProtocolAnswerByChecklistAnswerId(protocolItem?.answers, answerId)
        ?.isApplicable ?? true,
    [protocolItem?.answers]
  );

  const handleComplianceChange = useCallback(
    (answerId: string) =>
      (_: unknown, value: ComplianceAnswerOption) => {
        if (disabled) {
          return;
        }

        const formKey = getComplianceFormKey(answerId);
        setValues(formKey, value);
        flushPersistProtocolAnswer(answerId, AnswerType.Compliance, value);
      },
    [disabled, getComplianceFormKey, flushPersistProtocolAnswer, setValues]
  );

  const handleNumericChange = useCallback(
    (answerId: string) => (value: number | undefined) => {
      if (disabled) {
        return;
      }

      const formKey = getNumericFormKey(answerId);
      setValues(formKey, value);

      if (value === undefined) {
        flushPersistProtocolAnswer(answerId, AnswerType.Numeric, undefined);
        return;
      }

      trackPersistProtocolAnswer(answerId, AnswerType.Numeric, value);
    },
    [
      disabled,
      setValues,
      getNumericFormKey,
      trackPersistProtocolAnswer,
      flushPersistProtocolAnswer,
    ]
  );

  const handleNumericBlur = useCallback(
    (answerId: string) => () => {
      if (disabled) {
        return;
      }

      const formKey = getNumericFormKey(answerId);
      flushPersistProtocolAnswer(
        answerId,
        AnswerType.Numeric,
        valuesRef.current[formKey]
      );
    },
    [disabled, flushPersistProtocolAnswer, getNumericFormKey]
  );

  const handleNumericCommit = useCallback(
    (answerId: string) => (value: number) => {
      if (disabled) {
        return;
      }

      flushPersistProtocolAnswer(answerId, AnswerType.Numeric, value);
    },
    [disabled, flushPersistProtocolAnswer]
  );

  const handleTextChange = useCallback(
    (answerId: string) => (value: string) => {
      if (disabled) {
        return;
      }

      const formKey = getTextFormKey(answerId);
      setValues(formKey, value);
      trackPersistProtocolAnswer(answerId, AnswerType.Text, value);
    },
    [disabled, setValues, getTextFormKey, trackPersistProtocolAnswer]
  );

  const handleTextBlur = useCallback(
    (answerId: string) => () => {
      if (disabled) {
        return;
      }

      const formKey = getTextFormKey(answerId);
      flushPersistProtocolAnswer(
        answerId,
        AnswerType.Text,
        valuesRef.current[formKey]
      );
    },
    [disabled, flushPersistProtocolAnswer, getTextFormKey]
  );

  const handleOptionSelectionChange = useCallback(
    (answerId: string) => (value: string[]) => {
      if (disabled) {
        return;
      }

      const formKey = getOptionSelectionFormKey(answerId);
      setValues(formKey, value);
      flushPersistProtocolAnswer(answerId, AnswerType.OptionSelection, value);
    },
    [disabled, setValues, getOptionSelectionFormKey, flushPersistProtocolAnswer]
  );

  const seededAnswerIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    seededAnswerIdsRef.current = new Set();
  }, [checklistItemId]);

  useEffect(() => {
    if (!firstComplianceAnswer || protocolItem?.complianceCheck?.result === undefined) {
      return;
    }

    const formKey = getComplianceFormKey(firstComplianceAnswer._id);
    // Seed once from store; never overwrite a value the user already picked in the
    // form (including NOT_PASSED while backend is still UNSET after issue create).
    if (valuesRef.current[formKey] !== undefined) {
      return;
    }

    setValues(formKey, protocolItem.complianceCheck.result, { skipFormEdit: true });
  }, [
    firstComplianceAnswer,
    getComplianceFormKey,
    protocolItem?.complianceCheck?.result,
    setValues,
  ]);

  useEffect(() => {
    const protocolAnswers = protocolItem?.answers;
    if (!protocolAnswers || protocolAnswers.length === 0) return;

    protocolAnswers.forEach((answer: ProtocolAnswer) => {
      if (seededAnswerIdsRef.current.has(answer.checklistAnswerId)) return;

      if (isComplianceProtocolAnswer(answer) && answer.selectedOption !== undefined) {
        const formKey = getComplianceFormKey(answer.checklistAnswerId);
        if (valuesRef.current[formKey] === undefined) {
          setValues(formKey, answer.selectedOption, { skipFormEdit: true });
          seededAnswerIdsRef.current.add(answer.checklistAnswerId);
        }
      }

      if (isNumericProtocolAnswer(answer) && answer.numericValue !== undefined) {
        const formKey = getNumericFormKey(answer.checklistAnswerId);
        if (valuesRef.current[formKey] === undefined) {
          setValues(formKey, answer.numericValue, { skipFormEdit: true });
          seededAnswerIdsRef.current.add(answer.checklistAnswerId);
        }
      }

      if (isTextProtocolAnswer(answer) && answer.textValue !== undefined) {
        const formKey = getTextFormKey(answer.checklistAnswerId);
        if (valuesRef.current[formKey] === undefined) {
          setValues(formKey, answer.textValue, { skipFormEdit: true });
          seededAnswerIdsRef.current.add(answer.checklistAnswerId);
        }
      }

      if (isOptionSelectionProtocolAnswer(answer) && answer.selectedOptionIds !== undefined) {
        const formKey = getOptionSelectionFormKey(answer.checklistAnswerId);
        if (valuesRef.current[formKey] === undefined) {
          setValues(formKey, answer.selectedOptionIds, { skipFormEdit: true });
          seededAnswerIdsRef.current.add(answer.checklistAnswerId);
        }
      }
    });
  }, [
    protocolItem?.answers,
    setValues,
    getComplianceFormKey,
    getNumericFormKey,
    getTextFormKey,
    getOptionSelectionFormKey,
  ]);

  const hasAnswers = activeAnswers.length > 0;

  const renderNotApplicableToggle = (
    answerConfig: ChecklistItemAnswerModel
  ): ReactElement => {
    const isApplicable = isAnswerApplicable(answerConfig._id);

    return (
      <NotApplicableToggle
        isSkipped={!isApplicable}
        disabled={disabled}
        onClick={() =>
          void saveProtocolAnswerApplicability(
            checklistItemId,
            answerConfig._id,
            answerConfig.type,
            !isApplicable
          )
        }
      />
    );
  };

  const renderComplianceAnswer = (
    answerConfig: ComplianceChecklistAnswerModel
  ): ReactElement => {
    const answerLabel =
      answerConfig.label ||
      formatTranslation(intl, InspectionTranslationKeys.itemFormCompatibilityMessage);
    const complianceFormKey = getComplianceFormKey(answerConfig._id);
    const complianceValue = values[complianceFormKey] as
      | ComplianceAnswerOption
      | undefined;

    const applicable = isAnswerApplicable(answerConfig._id);
    const answerDisabled = disabled || !applicable;
    const displayComplianceValue = applicable
      ? (complianceValue ?? ComplianceAnswerOption.unset)
      : ComplianceAnswerOption.unset;

    return (
      <Box key={answerConfig._id} sx={answerStackSx}>
        <AnswerFieldHeader
          label={answerLabel}
          isRequired={answerConfig.isRequired}
          toggle={renderNotApplicableToggle(answerConfig)}
        />
        <Box sx={complianceToggleWrapSx}>
          <MemoForbiddenTooltip
            visibleWhen={disabled}
            reason={disabledReason}
          >
            <MemoComplianceAnswerOptionSelector
              checklistItemId={checklistItemId}
              handleChange={handleComplianceChange(answerConfig._id)}
              value={displayComplianceValue}
              optionLabels={answerConfig.optionLabels}
              disabled={answerDisabled}
            />
          </MemoForbiddenTooltip>
        </Box>
      </Box>
    );
  };

  const wrapDisabledAnswer = (
    answerId: string,
    answerInput: ReactElement
  ): ReactElement => (
    <MemoForbiddenTooltip
      key={answerId}
      visibleWhen={disabled}
      reason={disabledReason}
    >
      {answerInput}
    </MemoForbiddenTooltip>
  );

  const renderOtherAnswer = (
    answerConfig: ChecklistItemAnswerModel
  ): ReactElement | null => {
    const applicable = isAnswerApplicable(answerConfig._id);
    const answerDisabled = disabled || !applicable;
    const header = (
      <AnswerFieldHeader
        label={answerConfig.label}
        isRequired={answerConfig.isRequired}
        toggle={renderNotApplicableToggle(answerConfig)}
      />
    );

    if (isNumericChecklistAnswer(answerConfig)) {
      const formKey = getNumericFormKey(answerConfig._id);
      const displayNumericValue = applicable
        ? (values[formKey] as number | undefined)
        : undefined;
      return (
        <Box key={answerConfig._id} sx={answerStackSx}>
          {header}
          {wrapDisabledAnswer(
            answerConfig._id,
            <NumericAnswerInput
              answerConfig={answerConfig}
              value={displayNumericValue}
              onChange={handleNumericChange(answerConfig._id)}
              onBlur={handleNumericBlur(answerConfig._id)}
              onCommit={handleNumericCommit(answerConfig._id)}
              disabled={answerDisabled}
              hideLabel
            />
          )}
        </Box>
      );
    }

    if (isTextChecklistAnswer(answerConfig)) {
      const formKey = getTextFormKey(answerConfig._id);
      const displayTextValue = applicable
        ? ((values[formKey] as string | undefined) ?? '')
        : '';
      return (
        <Box key={answerConfig._id} sx={answerStackSx}>
          {header}
          {wrapDisabledAnswer(
            answerConfig._id,
            <TextAnswerInput
              answerConfig={answerConfig}
              value={displayTextValue}
              onChange={handleTextChange(answerConfig._id)}
              onBlur={handleTextBlur(answerConfig._id)}
              disabled={answerDisabled}
              hideLabel
            />
          )}
        </Box>
      );
    }

    if (isOptionSelectionChecklistAnswer(answerConfig)) {
      const formKey = getOptionSelectionFormKey(answerConfig._id);
      const displayOptionSelectionValue = applicable
        ? ((values[formKey] as string[] | undefined) ?? [])
        : [];
      return (
        <Box key={answerConfig._id} sx={answerStackSx}>
          {header}
          {wrapDisabledAnswer(
            answerConfig._id,
            <OptionSelectionInput
              answerConfig={answerConfig}
              value={displayOptionSelectionValue}
              onChange={handleOptionSelectionChange(answerConfig._id)}
              disabled={answerDisabled}
              hideLabel
            />
          )}
        </Box>
      );
    }

    return null;
  };

  return (
    <Box sx={containerSx}>
      {hasAnswers ? (
        <Box sx={answersSectionSx}>
          {complianceAnswers.map(renderComplianceAnswer)}
          {otherAnswers.map(renderOtherAnswer)}
        </Box>
      ) : null}

      <Box sx={commentSectionSx}>
        <ProtocolItemCommentAndAttachments
          checklistItemId={checklistItemId}
          allExpanded={allExpanded}
        />
      </Box>
    </Box>
  );
}
